import React, { useEffect } from 'react';
import { useViewStateStore } from '../../stores/viewState.store';
import { usePortletStore } from '../../App';
import { decodeEscapedHtml } from 'virtual-visit-common';
import { Button, intl, SecurityForm } from 'ehs-react';

export function Disclaimer(): JSX.Element {
  const [
    recaptchaVerified,
    securityFormVerified,
    nextState,
    loggedInPatient,
    setVVSecForm,
    updateSecurityForm
  ] = useViewStateStore(state => [
    state.recaptchaVerified,
    state.securityFormVerified,
    state.nextState,
    state.viewStateData?.loggedInPatient,
    state.setVVSecForm,
    state.updateSecurityForm
  ]);

  /**
   * Data for the honeypot.
   */
  type secDataForm = {
    firstName: string;
    lastName: string;
    email: string;
  };
  const formCreationTime = new Date().getTime();
  const [
    disclaimer,
    disableCaptcha,
    securityFormDisabled,
    hideRecaptcha
  ] = usePortletStore(state => [
    state.configuration.disclaimer,
    state.configuration.disableGoogleRecaptcha,
    state.configuration.securityFormDisabled,
    state.configuration.urlParameters.hideRecaptcha
  ]);

  const onContinueClick = () => {
    updateSecurityForm(formCreationTime);
    nextState();
  };

  const onSecurityInputChange = (values: secDataForm) => {
    setVVSecForm({
      lastName: values.lastName ?? '',
      firstName: values.firstName ?? '',
      email: values.email ?? '',
      period: formCreationTime
    });
  };

  useEffect(() => {
    const disclCont = document.getElementById('disclaimerContainer');
    if (disclCont) {
      disclCont.innerHTML = decodeEscapedHtml(disclaimer);
    }
  }, [disclaimer]);

  return (
    <>
      <div id="disclaimerContainer" className="vvpp-disclaimer" />
      {!disableCaptcha && !loggedInPatient && (
        <>
          <GoogleCaptcha />
          <GoogleReCaptchaScript />
        </>
      )}
      {!securityFormDisabled && !loggedInPatient && (
        <SecurityForm
          onChange={values => {
            onSecurityInputChange(values);
          }}
        />
      )}
      <div className="vvpp-single-button">
        <Button
          id="vvpp-disclaimer-accept-button"
          variant="primary"
          disabled={
            (!recaptchaVerified &&
              !disableCaptcha &&
              !loggedInPatient &&
              !hideRecaptcha) ||
            (!securityFormVerified && !loggedInPatient)
          }
          onClick={onContinueClick}
          text={intl('virtualvisitpatientportlet.disclaimer.button')}
        />
      </div>
    </>
  );
}

function GoogleCaptcha(): JSX.Element {
  const [
    disableRecaptcha,
    googleSiteKey,
    hideRecaptcha
  ] = usePortletStore(state => [
    state.configuration.disableGoogleRecaptcha,
    state.configuration.googleSiteKey,
    state.configuration.urlParameters.hideRecaptcha
  ]);

  const [verifyGoogleRecaptcha] = useViewStateStore(state => [
    state.verifyGoogleRecaptcha
  ]);

  useEffect(() => {
    if (!disableRecaptcha && !hideRecaptcha) {
      if (!(window as any).onGoogleVerified) {
        (window as any).onGoogleVerified = function (
          recaptchaResponse: string
        ) {
          console.log('recaptchaResponse', recaptchaResponse);
          verifyGoogleRecaptcha(recaptchaResponse);
        };
      }
    }
  }, [disableRecaptcha, verifyGoogleRecaptcha, hideRecaptcha]);

  return (
    <div
      className="g-recaptcha"
      data-sitekey={googleSiteKey}
      data-callback="onGoogleVerified"
    />
  );
}

function GoogleReCaptchaScript(): JSX.Element {
  const [disableGoogleRecaptcha, hideRecaptcha] = usePortletStore(state => [
    state.configuration.disableGoogleRecaptcha,
    state.configuration.urlParameters.hideRecaptcha
  ]);

  useEffect(() => {
    if (!disableGoogleRecaptcha && !hideRecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.onload = () => {
        console.log('recaptcha script loaded');
      };
      document.body.appendChild(script);
    }
  }, [disableGoogleRecaptcha, hideRecaptcha]);

  return <React.Fragment />;
}
